import React from 'react';
import { useGetPage } from 'hooks/useGetPage';
import { pageIds } from 'utilities/constants';
import { twMerge } from 'tailwind-merge';
import { useGetRecommendedProduct } from './useGetRecommendedProduct';
import Loader from 'components/loaderComponent';
import RecommendedProductCard from './RecommendedProductCard';
import {
  GetFhirRecommendationOutput,
  ServiceRequestStatus,
} from 'graphql/generated/hasura';
import { Card } from '../../Card';

interface IRecommendedProductProps {
  providerId: string;
  recommendations: GetFhirRecommendationOutput[];
}

const RecommendedProducts: React.FC<IRecommendedProductProps> = ({
  providerId,
  recommendations,
}) => {
  const { data: locale, loading: isLocaleLoading } = useGetPage({
    locale: 'en',
    pageId: pageIds.SHOP_DIAGNOSTIC,
  });

  const { loading, isProvider, handleOnPurchase } = useGetRecommendedProduct({
    providerId,
  });

  if (loading || isLocaleLoading) return <Loader />;

  if (!recommendations.length) {
    return (
      <Card
        title={
          isProvider
            ? locale?.recommendedTitleProvider
            : locale?.recommendedTitlePatient ?? 'Suggest Diagnostic test kits'
        }
        titleClassName="flex text-h4 text-btn-black font-semibold"
        backgroundColor="bg-white"
        showSeparator
      >
        <p
          className={twMerge(
            'text-med-gray-3 font-medium py-4 hidden',
            isProvider && 'block',
          )}
        >
          No Diagnostic test kits selected. In this section, you can suggest
          Diagnostic test kits for the patient{' '}
        </p>
        <p
          className={twMerge(
            'text-med-gray-3 font-medium py-4 block',
            isProvider && 'hidden',
          )}
        >
          No Diagnostic test kits selected.
        </p>
      </Card>
    );
  }

  return (
    <>
      <div className="bg-white desktop:p-30 p-5 flex flex-col gap-5 desktop:gap-30 rounded-10">
        <div
          className={twMerge(
            'flex desktop:flex-row flex-col w-full desktop:justify-between desktop:items-center border-b border-gray-opacity-15 pb-2.5',
          )}
        >
          <div className="text-neutral-800 font-semibold text-xl">
            {isProvider
              ? locale?.recommendedTitleProvider
              : locale?.recommendedTitlePatient ??
                'Suggest Diagnostic test kits'}
          </div>
        </div>

        <div className={twMerge('flex flex-col gap-2.5')}>
          {recommendations.map(
            ({ quantity, productId, productTitle, totalPrice, status, id }) => (
              <RecommendedProductCard
                title={productTitle as string}
                total={Math.round(totalPrice / 100)}
                amount={quantity}
                isProvider={isProvider}
                loading={loading}
                isPurchase={status === ServiceRequestStatus.Completed}
                onPurchase={() => handleOnPurchase(productId, quantity, id)}
              />
            ),
          )}
        </div>
      </div>
    </>
  );
};

export default RecommendedProducts;
